import { Suspense, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Header } from "@/layouts/header"
import { ShiftPlanFilters } from "@/modules/shift-plan-overview/components/shift-plan-filters"
import { ShiftTimeline } from "@/modules/shift-plan-overview/components/shift-timeline"
import { useShiftPlanOverviewQuery } from "@/modules/shift-plan-overview/services/useShiftPlanOverviewQuery"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"
import type { ShiftPlanRoleFilter } from "@/types/__generated__/union-types"

const FallbackLoading = () => (
  <div className="text-on-surface-subdued flex h-64 items-center justify-center text-sm">
    <LoadingSpinner isFullPage size={24} />
  </div>
)

const ShiftPlanContent = ({ roleFilter }: { roleFilter: ShiftPlanRoleFilter }) => {
  const { t } = useTranslation()
  const updateAvailableOptions = useShiftPlanFiltersStore((state) => state.actions.updateAvailableOptions)
  const filters = useShiftPlanFiltersStore((state) => state.filters)
  const selectedDate = useShiftPlanFiltersStore((state) => state.selectedDate)
  const data = useShiftPlanOverviewQuery(format(selectedDate, "yyyy-MM-dd"), roleFilter, filters)

  useEffect(() => {
    updateAvailableOptions(data.shiftPlanOverview)
  }, [data.shiftPlanOverview, updateAvailableOptions])

  if (data.shiftPlanOverview.length === 0) {
    return (
      <div className="text-on-surface-subdued flex h-64 items-center justify-center text-sm">
        {t("shiftPlanOverview.noShiftsFound")}
      </div>
    )
  }

  return <ShiftTimeline agents={data.shiftPlanOverview} />
}

export const ShiftPlanOverview = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<ShiftPlanRoleFilter | string>("DOCTORS_AND_MEDICAL_SUPERVISORS")
  const resetFilters = useShiftPlanFiltersStore((state) => state.actions.resetFilters)

  const handleTabChange = useCallback(
    (value: string) => {
      resetFilters(true)
      setActiveTab(value)
    },
    [resetFilters],
  )

  // Reset filter when navigating to other pages
  useEffect(() => () => resetFilters(), [resetFilters])

  return (
    <div className="row-span-full flex h-full min-h-0 flex-col">
      <Header title={t("shiftPlanOverview.pageTitle")} />
      <div className="flex min-h-0 flex-1 flex-col p-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col">
          <TabsList variant="secondary">
            <TabsTrigger value="DOCTORS_AND_MEDICAL_SUPERVISORS" variant="secondary">
              {t("shiftPlanOverview.tabs.doctorsAndMedicalSupervisors")}
            </TabsTrigger>
            <TabsTrigger value="NURSING_PROFESSIONALS" variant="secondary">
              {t("shiftPlanOverview.tabs.nursing")}
            </TabsTrigger>
            <TabsTrigger value="CALL_CENTER_AGENTS" variant="secondary">
              {t("shiftPlanOverview.tabs.callCenterAgents")}
            </TabsTrigger>
          </TabsList>
          <div className="my-4">
            <ShiftPlanFilters activeTab={activeTab} />
          </div>
          <TabsContent className="min-h-0 flex-1 p-0" value="DOCTORS_AND_MEDICAL_SUPERVISORS">
            <Suspense fallback={<FallbackLoading />}>
              <ShiftPlanContent roleFilter="DOCTORS_AND_MEDICAL_SUPERVISORS" />
            </Suspense>
          </TabsContent>
          <TabsContent className="min-h-0 flex-1 p-0" value="NURSING_PROFESSIONALS">
            <Suspense fallback={<FallbackLoading />}>
              <ShiftPlanContent roleFilter="NURSING_PROFESSIONALS" />
            </Suspense>
          </TabsContent>
          <TabsContent className="min-h-0 flex-1 p-0" value="CALL_CENTER_AGENTS">
            <Suspense fallback={<FallbackLoading />}>
              <ShiftPlanContent roleFilter="CALL_CENTER_AGENTS" />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
